import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

/**
 * Global keyboard shortcuts. Mounted once from AppShell so every
 * authenticated page gets them. Ignored while the user is typing
 * in an input / textarea (so Cmd+K inside the composer doesn't
 * navigate away — there's none of those shortcuts for composer
 * input right now, but the invariant keeps things safe).
 */
export function useShortcuts(): void {
  const navigate = useNavigate();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      // Shortcuts that navigate — fine to intercept from inputs too.
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        void navigate({ to: '/search' });
        return;
      }
      if (e.key === '/') {
        // Cmd+/ focuses the composer; don't steal from it if already
        // focused (native browsers handle repeat keydown).
        const ta = document.querySelector<HTMLTextAreaElement>(
          'textarea[aria-label="Message composer"]',
        );
        if (ta) {
          e.preventDefault();
          ta.focus();
        }
        return;
      }
      if ((e.key === 'n' || e.key === 'N') && e.shiftKey) {
        e.preventDefault();
        void navigate({ to: '/new' });
        return;
      }
      if (e.key === ',') {
        e.preventDefault();
        void navigate({ to: '/settings' });
        return;
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);
}
